import { useEffect, useState } from 'react';
import { fetchWeatherByAddress, type WeatherInfo } from '../../services/weather';
import './WeatherWidget.css';

// Mapeo de códigos WMO a familia de iconos. Espejo de PositionAdapter.kt
// `weatherIconFor` (Android). Códigos:
// https://www.open-meteo.com/en/docs#weathervariables
type WeatherIconKind = 'sun' | 'cloud' | 'fog' | 'rain' | 'snow' | 'storm';
function iconKindFor(code: number): WeatherIconKind {
  if (code === 0 || code === 1) return 'sun';
  if (code === 2 || code === 3) return 'cloud';
  if (code === 45 || code === 48) return 'fog';
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82)) return 'rain';
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return 'snow';
  if (code >= 95 && code <= 99) return 'storm';
  return 'cloud';
}

// Iconos SVG inline — minimalistas, monocromos. Heredan currentColor.
function WeatherIcon({ kind }: { kind: WeatherIconKind }) {
  switch (kind) {
    case 'sun':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M12 7c-2.76 0-5 2.24-5 5s2.24 5 5 5 5-2.24 5-5-2.24-5-5-5zm-7 5H2v2h3v-2zm17 0h-3v2h3v-2zM12 2h-2v3h2V2zm0 17h-2v3h2v-3zM5.64 4.22L4.22 5.64l2.12 2.12 1.41-1.41-2.11-2.13zm12.72 12.72l-1.41 1.41 2.12 2.12 1.41-1.41-2.12-2.12zm0-12.72l-2.12 2.13 1.41 1.41 2.12-2.12-1.41-1.42zM5.64 19.78l2.12-2.12-1.41-1.41-2.13 2.12 1.42 1.41z"/>
        </svg>
      );
    case 'cloud':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
        </svg>
      );
    case 'fog':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M3 15h18v2H3v-2zm0 4h12v2H3v-2zm0-8h18v2H3v-2zm6-6c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2zm-3 4c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm12 0c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3z" opacity=".7"/>
          <path d="M3 11h18v2H3z"/>
        </svg>
      );
    case 'rain':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M17 5.07A7.5 7.5 0 005.35 9.04 5 5 0 006 19h11a4.5 4.5 0 000-9V5.07z"/>
          <path d="M8 22l1.5-3M12 22l1.5-3M16 22l1.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" fill="none"/>
        </svg>
      );
    case 'snow':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"/>
          <circle cx="8" cy="21" r="1"/><circle cx="12" cy="21.5" r="1"/><circle cx="16" cy="21" r="1"/>
        </svg>
      );
    case 'storm':
      return (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true">
          <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h2l-1.5 3 4.5-4.5h2l-1 3 4-5h-2.5l1-2H10l3-4z"/>
        </svg>
      );
  }
}

// Widget que muestra el clima de una fábrica para una fecha determinada.
// Es opt-in vía props: si no se le pasa address o date, no se renderiza.
// Si la API falla o devuelve null, el componente no muestra nada (no
// hay error UI — el clima es información secundaria).
export function WeatherWidget({ address, date }: { address?: string; date?: string }) {
  const [info, setInfo] = useState<WeatherInfo | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!address || !date) {
      setInfo(null);
      return;
    }
    fetchWeatherByAddress(address, date).then((res) => {
      if (!cancelled) setInfo(res);
    });
    return () => { cancelled = true; };
  }, [address, date]);

  if (!info) return null;
  return (
    <span className="weather-widget" aria-label={`Clima ${Math.round(info.temperatureC)} grados`}>
      <WeatherIcon kind={iconKindFor(info.weatherCode)} />
      <span className="weather-widget__temp">{Math.round(info.temperatureC)}°</span>
    </span>
  );
}
